import { useCallback, useEffect, useRef, type MutableRefObject } from 'react'

import type { MediaPlayerInstance } from '@vidstack/react'

import {
    createListenerRegistry,
    getTargetOrigin,
    parseRequest,
    PLAYER_JS_EVENTS,
    PLAYER_JS_METHODS,
    serializeResponse,
    SUPPORTED_EVENTS,
    SUPPORTED_METHODS,
    type PlayerJsRequest,
} from 'modules/player-js'

type Props = {
    playerRef: MutableRefObject<MediaPlayerInstance | null>
    isMounted: boolean
}

type PostArgs = {
    event: string
    value?: unknown
    listener?: string
}

const clampToRange = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

const toFiniteNumber = (value: unknown, fallback: number): number => {
    const num = Number(value)
    return Number.isFinite(num) ? num : fallback
}

const buildReadyPayload = () => ({
    src: window.location.toString(),
    events: SUPPORTED_EVENTS,
    methods: SUPPORTED_METHODS,
})

export const usePlayerJsReceiver = ({ playerRef, isMounted }: Props) => {
    const registryRef = useRef(createListenerRegistry())
    const isReadyRef = useRef(false)
    const targetOriginRef = useRef<string>(getTargetOrigin())

    const postToParent = useCallback(({ event, value, listener }: PostArgs) => {
        const msg = serializeResponse({ event, value, listener })
        window.parent.postMessage(msg, targetOriginRef.current)
    }, [])

    const notifyListeners = useCallback(
        (event: string, value?: unknown) => {
            const listeners = registryRef.current.getAll(event)
            listeners.forEach(listener => postToParent({ event, value, listener }))
            return listeners.length > 0
        },
        [postToParent],
    )

    const handleRequest = useCallback(
        (request: PlayerJsRequest) => {
            const player = playerRef.current
            const { method, value, listener } = request

            switch (method) {
                case PLAYER_JS_METHODS.PLAY:
                    if (!player) return
                    player.play().catch(error => {
                        console.error('player.js: initial play() rejected, retrying muted', error)
                        player.muted = true
                        player.play().catch(retryError => {
                            console.error('player.js: muted play() retry also rejected', retryError)
                        })
                    })
                    return
                case PLAYER_JS_METHODS.PAUSE:
                    if (!player) return
                    player.pause()
                    return
                case PLAYER_JS_METHODS.MUTE:
                    if (!player) return
                    player.muted = true
                    return
                case PLAYER_JS_METHODS.UNMUTE:
                    if (!player) return
                    player.muted = false
                    return
                case PLAYER_JS_METHODS.SET_CURRENT_TIME:
                    if (!player) return
                    player.currentTime = toFiniteNumber(value, 0)
                    return
                case PLAYER_JS_METHODS.SET_VOLUME:
                    if (!player) return
                    player.volume = clampToRange(toFiniteNumber(value, 0) / 100, 0, 1)
                    return
                case PLAYER_JS_METHODS.SET_LOOP:
                    if (!player) return
                    player.remoteControl.userPrefersLoopChange(Boolean(value))
                    return

                case PLAYER_JS_METHODS.GET_PAUSED:
                    postToParent({ event: method, value: player?.paused ?? true, listener })
                    return
                case PLAYER_JS_METHODS.GET_CURRENT_TIME:
                    postToParent({ event: method, value: player?.currentTime ?? 0, listener })
                    return
                case PLAYER_JS_METHODS.GET_DURATION: {
                    const duration = player?.state.duration ?? 0
                    postToParent({ event: method, value: Number.isFinite(duration) ? duration : 0, listener })
                    return
                }
                case PLAYER_JS_METHODS.GET_MUTED:
                    postToParent({ event: method, value: !!player?.muted, listener })
                    return
                case PLAYER_JS_METHODS.GET_VOLUME:
                    postToParent({ event: method, value: Math.round((player?.volume ?? 0) * 100), listener })
                    return
                case PLAYER_JS_METHODS.GET_LOOP:
                    postToParent({ event: method, value: !!player?.state.userPrefersLoop, listener })
                    return

                case PLAYER_JS_METHODS.ADD_EVENT_LISTENER: {
                    if (typeof value !== 'string' || !listener) return
                    registryRef.current.add(value, listener)
                    if (value === PLAYER_JS_EVENTS.READY && isReadyRef.current) {
                        postToParent({ event: PLAYER_JS_EVENTS.READY, value: buildReadyPayload(), listener })
                    }
                    return
                }
                case PLAYER_JS_METHODS.REMOVE_EVENT_LISTENER: {
                    if (typeof value !== 'string') return
                    if (listener) {
                        registryRef.current.remove(value, listener)
                        return
                    }
                    registryRef.current.removeAll(value)
                    return
                }

                default:
                    notifyListeners(PLAYER_JS_EVENTS.ERROR, { code: 2, msg: `Invalid Method "${method}"` })
            }
        },
        [postToParent, notifyListeners, playerRef],
    )

    useEffect(() => {
        const onMessage = (e: MessageEvent) => {
            const request = parseRequest({ data: e.data })
            if (!request) return
            handleRequest(request)
        }

        window.addEventListener('message', onMessage)
        return () => window.removeEventListener('message', onMessage)
    }, [handleRequest])

    useEffect(() => {
        if (!isMounted) return
        const player = playerRef.current
        if (!player) return

        const emitReadyOnce = () => {
            if (isReadyRef.current) return
            isReadyRef.current = true
            const payload = buildReadyPayload()
            const hadListeners = notifyListeners(PLAYER_JS_EVENTS.READY, payload)
            if (!hadListeners) {
                postToParent({ event: PLAYER_JS_EVENTS.READY, value: payload })
            }
        }

        const onCanPlay = () => emitReadyOnce()
        const onPlay = () => notifyListeners(PLAYER_JS_EVENTS.PLAY)
        const onPause = () => notifyListeners(PLAYER_JS_EVENTS.PAUSE)
        const onEnded = () => notifyListeners(PLAYER_JS_EVENTS.ENDED)
        const onSeeked = () => notifyListeners(PLAYER_JS_EVENTS.SEEKED)
        const onTimeUpdate = () => {
            const duration = player.state.duration
            notifyListeners(PLAYER_JS_EVENTS.TIME_UPDATE, {
                seconds: player.currentTime,
                duration: Number.isFinite(duration) ? duration : 0,
            })
        }
        const onProgress = () => {
            const duration = player.state.duration
            const bufferedEnd = player.state.bufferedEnd
            notifyListeners(PLAYER_JS_EVENTS.PROGRESS, {
                seconds: Number.isFinite(bufferedEnd) ? bufferedEnd : 0,
                duration: Number.isFinite(duration) ? duration : 0,
            })
        }
        const onError = (event: Event) => {
            const detail = (event as CustomEvent<{ code?: number; message?: string }>).detail
            notifyListeners(PLAYER_JS_EVENTS.ERROR, {
                code: detail?.code ?? -1,
                msg: detail?.message ?? '',
            })
        }

        player.addEventListener('can-play', onCanPlay)
        player.addEventListener('play', onPlay)
        player.addEventListener('pause', onPause)
        player.addEventListener('ended', onEnded)
        player.addEventListener('seeked', onSeeked)
        player.addEventListener('time-update', onTimeUpdate)
        player.addEventListener('progress', onProgress)
        player.addEventListener('error', onError)

        if (player.state.canPlay) emitReadyOnce()

        return () => {
            player.removeEventListener('can-play', onCanPlay)
            player.removeEventListener('play', onPlay)
            player.removeEventListener('pause', onPause)
            player.removeEventListener('ended', onEnded)
            player.removeEventListener('seeked', onSeeked)
            player.removeEventListener('time-update', onTimeUpdate)
            player.removeEventListener('progress', onProgress)
            player.removeEventListener('error', onError)
        }
    }, [isMounted, playerRef, notifyListeners, postToParent])
}
